/**
 * PATH: packages/ui/src/kanban/components/settings-home/AllKanbanSettingsFrames.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-ALL-FRAMES-001
 * PURPOSE: Reuse All-Kanban page bodies without a board overlay or duplicated JSX.
 * ROLE IN ARCHITECTURE: Shared frames for settings-hub and SettingsHome.
 * MAIN EXPORTS: AllKanbanSettingsFrames.
 * NON-RESPONSIBILITIES: Does not invent a second SettingsHome body.
 * NOTES FOR FUTURE AI: Board pages stay behind hasBoardContext. Embedded chrome
 *   is for the settings-hub tab; the board overlay already has a shell.
 */

import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'

import { trackKanbanSettingsEvent } from '../../../diagnostics/settingsTelemetry'
import { SettingsActivityUndo } from './chrome'
import { useOptionalKanbanSettingsPreferences } from '../../preferences/KanbanSettingsPreferenceContext'
import { KanbanSettingsPreferenceProvider } from '../../preferences/KanbanSettingsPreferenceProvider'
import { useKanbanCardFacePrefs } from '../board-view/cardFacePrefs'
import type { CardDensity } from '../board-view/cardDensity'
import { KANBAN_SETTINGS_PREFERENCE_DEFINITIONS } from '../board-view/kanbanSettingsPreferenceDefinitions'
import type { KanbanApi } from '../../api'
import type { Board } from '../../types'
import type { SettingsCapabilities } from './contract'
import { DrawerSettingsPage } from './DrawerSettingsPage'
import { FaceSettingsPage } from './FaceSettingsPage'
import { KanbanSettingsPage } from './KanbanSettingsPage'
import type { SettingsPage } from './nav'
import { SettingsHomeShell } from './SettingsHomeShell'
import { SettingsStatusRibbon } from './SettingsStatusRibbon'
import { useSettingsUndo } from './useSettingsUndo'
import { useSettingsViewport } from './useSettingsViewport'

const ALL_PAGES: SettingsPage[] = ['kanban-face', 'kanban-drawer', 'kanban-general']

export function AllKanbanSettingsFrames({
  page,
  onPage,
  capabilities,
  userId,
  api,
  organisationId,
  board,
  cardDensity,
  setCardDensity,
  keyboardMode,
  setKeyboardMode,
  setShortcutsOpen,
  onClose,
  embedded = false,
}: {
  page: SettingsPage
  onPage: (page: SettingsPage) => void
  capabilities: SettingsCapabilities
  userId: string | number | null
  api?: KanbanApi
  organisationId?: number
  board?: Board
  cardDensity: CardDensity
  setCardDensity: (next: CardDensity) => void
  keyboardMode: boolean
  setKeyboardMode: (next: boolean) => void
  setShortcutsOpen: Dispatch<SetStateAction<boolean>>
  onClose: () => void
  embedded?: boolean
}) {
  const existing = useOptionalKanbanSettingsPreferences()
  const bodyProps = {
    page, onPage, capabilities, userId, api, organisationId, board, cardDensity,
    setCardDensity, keyboardMode, setKeyboardMode, setShortcutsOpen, onClose, embedded,
  }
  if (existing) {
    return <AllKanbanSettingsBody {...bodyProps} />
  }
  return (
    <KanbanSettingsPreferenceProvider
      boardId={board?.id ?? ''}
      boardKind={board?.kind}
      userId={userId}
      definitions={KANBAN_SETTINGS_PREFERENCE_DEFINITIONS}
    >
      <AllKanbanSettingsBody {...bodyProps} />
    </KanbanSettingsPreferenceProvider>
  )
}

function AllKanbanSettingsBody({
  page,
  onPage,
  capabilities,
  userId,
  api,
  organisationId,
  board,
  cardDensity,
  setCardDensity,
  keyboardMode,
  setKeyboardMode,
  setShortcutsOpen,
  onClose,
  embedded,
}: {
  page: SettingsPage
  onPage: (page: SettingsPage) => void
  capabilities: SettingsCapabilities
  userId: string | number | null
  api?: KanbanApi
  organisationId?: number
  board?: Board
  cardDensity: CardDensity
  setCardDensity: (next: CardDensity) => void
  keyboardMode: boolean
  setKeyboardMode: (next: boolean) => void
  setShortcutsOpen: Dispatch<SetStateAction<boolean>>
  onClose: () => void
  embedded: boolean
}) {
  const [prefs, setPrefs] = useKanbanCardFacePrefs(userId)
  const undo = useSettingsUndo(prefs)
  const viewport = useSettingsViewport()
  const [phoneShowingNav, setPhoneShowingNav] = useState(true)
  const safePage = ALL_PAGES.includes(page) ? page : 'kanban-face'
  const [density, setDensity] = useState(cardDensity)
  useEffect(() => {
    setDensity(cardDensity)
  }, [cardDensity])

  if (!capabilities.hasBoardContext && !ALL_PAGES.includes(page)) {
    return (
      <p data-testid="settings-all-kanban-need-board" className="px-4 py-6 text-sm text-neutral-500">
        Open a board to edit This-board settings. All Kanban defaults stay here.
      </p>
    )
  }

  const goPage = (next: SettingsPage) => {
    trackKanbanSettingsEvent('kanban.page_change', { controlId: next, scope: 'global' })
    onPage(next)
    setPhoneShowingNav(false)
  }

  const body = (
    <div data-testid="settings-all-kanban-frames">
      {(safePage === 'kanban-face' || page === 'board-face') && (
        <FaceSettingsPage
          page={safePage}
          onPage={goPage}
          board={board}
          prefs={prefs}
          setPrefs={(next) => {
            undo.remember(prefs)
            setPrefs(next)
            trackKanbanSettingsEvent('kanban.face_save', {
              controlId: safePage,
              scope: 'global',
              status: 'pending',
            })
          }}
          cardDensity={density}
          setCardDensity={(next) => {
            setDensity(next)
            setCardDensity(next)
          }}
          allowBoardScope={capabilities.hasBoardContext}
        />
      )}
      {(safePage === 'kanban-drawer' || page === 'board-drawer') && (
        <DrawerSettingsPage
          page={safePage}
          onPage={goPage}
          board={board}
          allowBoardScope={capabilities.hasBoardContext}
        />
      )}
      {safePage === 'kanban-general' && (
        <KanbanSettingsPage
          userId={userId}
          api={api}
          organisationId={organisationId ?? board?.organisation}
          keyboardMode={keyboardMode}
          setKeyboardMode={setKeyboardMode}
          setShortcutsOpen={setShortcutsOpen}
          onClose={onClose}
          capabilities={capabilities}
        />
      )}
    </div>
  )

  if (!embedded) return body

  return (
    <SettingsHomeShell
      mode="embedded"
      viewport={viewport}
      page={safePage}
      onPage={goPage}
      onClose={onClose}
      phoneShowingNav={phoneShowingNav}
      onPhoneBack={() => setPhoneShowingNav(true)}
      navGroups={['kanban']}
      status={<SettingsStatusRibbon />}
    >
      {undo.canUndo ? (
        <SettingsActivityUndo
          onUndo={() => {
            const previous = undo.undo()
            if (previous) setPrefs(previous)
          }}
        />
      ) : null}
      {body}
    </SettingsHomeShell>
  )
}
